import { getManagementFeaturePayload } from '@/lib/hook/managementPreferences'
import { getListRouteAvailableWithoutEntity } from '@/lib/routes/getAllRoutes'
import { EntityList } from '@/types/Entity'

// Feature de gestion (managementPreferences) portant la page de demarrage post-login.
export const START_PAGE_FEATURE = 'start_page'
export const START_PAGE_CATEGORY = 'ui'

// Payload persiste cote API : { route: <nom unique d'une route front> | null }.
// route null (ou absent) = comportement par defaut (aucune preference).
export type StartPagePayload = { route?: string | null }

type StartPageTarget = { href: string; as: string; entity: EntityList | null }

/** Une route n'est atterrissable directement que si son href ne contient pas de placeholder [param]. */
const isLandableHref = (href?: string): href is string => !!href && !href.includes('[')

/**
 * Resout la preference "page de demarrage" en une cible {href, as, entity} atterrissable,
 * ou `null` si : aucune preference, route devenue inaccessible (roles modifies / route supprimee),
 * ou route a placeholder (non atterrissable directement).
 *
 * Le filtrage par roles se fait via getListRouteAvailableWithoutEntity(loggedUserRoles) : une route
 * dont l'utilisateur n'a plus les roles n'y figure pas -> fallback au comportement par defaut.
 */
export const resolveStartPageRoute = (
  dataMyProfile: any,
  loggedUserRoles: string[]
): StartPageTarget | null => {
  const payload = getManagementFeaturePayload<StartPagePayload>(dataMyProfile, START_PAGE_FEATURE)
  const routeName = payload?.route
  if (!routeName || typeof routeName !== 'string') return null

  const match = getListRouteAvailableWithoutEntity(loggedUserRoles).find(
    (r: any) => r.route === routeName
  )
  if (!match || !isLandableHref(match.href)) return null

  return {
    href: match.href,
    as: match.href,
    entity: Array.isArray(match.entity) ? (match.entity[0] ?? null) : (match.entity ?? null),
  }
}

export type StartPageOption = { value: string; label: string }

/**
 * Liste des pages proposables comme page de demarrage pour l'utilisateur courant :
 * routes de menu (menuHeader) reellement disponibles (filtrees par roles), non desactivees et
 * sans placeholder. Dedoublonnees par nom de route et triees par libelle traduit (fallback `text`).
 */
export const getStartPageOptions = (
  loggedUserRoles: string[],
  t: (key: string) => string
): StartPageOption[] => {
  const seen = new Set<string>()

  return getListRouteAvailableWithoutEntity(loggedUserRoles)
    .filter(
      (r: any) => r?.menuHeader === true && !r?.disabled && isLandableHref(r?.href) && !!r?.route
    )
    .filter((r: any) => {
      if (seen.has(r.route)) return false
      seen.add(r.route)
      return true
    })
    .map((r: any) => ({ value: r.route, label: r.t ? t(r.t) : r.text }))
    .sort((a, b) => a.label.localeCompare(b.label))
}
